import * as React from 'react';
import { flatten } from 'rambda';

import { Product, Refinements, ClickoutSegment, isBrowser, LocalDealerData, AlgoliaIndiceName } from '@packages/common';
import { AlgoliaContext, mapRefinementsToAlgoliaQuery } from '@packages/services';
import { useDyClient } from '@lib/dy';

import { gtmEventDispatcher } from '../GTMContext/gtmEventDispatcher';
import { MarketingEventPayload, MerchantAdClickedEvent } from '../GTMContext/events';
import { useDynamicYieldApiEvents, useDynamicYieldEvents } from '../DynamicYield';
import { useRefinementsEvents } from '../RefinementsContext';
import { ProductsContext } from '../ProductsContext';
import { useNewsletterCallbacks } from '../../containers/NewsletterFooterContainer';
import { useABTestingConfig } from '../abTestingContext';

type ProductClickoutParams = {
  product: Product;
  segment: ClickoutSegment;
  trackingToken?: string;
  queryID?: string;
  indexName: string;
  index: number;
};

interface SideEffectsContextValue {
  addWishlist: (product: Product) => void;
  removeFromWishlist: (product: Product) => void;
  merchantAdLoad: (merchantAdData: LocalDealerData) => void;
  merchantClickExternal: (event: MerchantAdClickedEvent) => void;
  merchantClickInternal: (event: MerchantAdClickedEvent) => void;
  searchSubmit: (searchTerm: string) => void;
  searchQuerySuggestionSubmit: (searchTerm: string) => void;
  productClickout: (params: ProductClickoutParams) => void;
  marketing: (payload: MarketingEventPayload) => void;
  productLoadMore: (currentPage: number) => void;
  productSearchStateUpdate: (refinements: Refinements) => void;
  recommendationRequest: (service: string, prudsysTestGroup: string) => void;
  cmsModuleEvent: (data: { [key: string]: any }) => void;
  newsletterRegistered: () => void;
}

export const SideEffectsContext = React.createContext({} as SideEffectsContextValue);

export const SideEffectsProvider: React.FC = ({ children }) => {
  const { trackingAgentBySearchIndex } = React.useContext(AlgoliaContext);
  const { productSearchResponse } = React.useContext(ProductsContext);
  const { PRODUCTS_PERSONALIZATION_PROVIDER } = useABTestingConfig();
  const { client: dyApiClient, context } = useDyClient();
  const dynamicYieldApi = useDynamicYieldApiEvents();
  const dynamicYield = useDynamicYieldEvents();
  const { clearAll$, priceSort$, refinementsChangedDeep$, facetFilter$, numericFilter$, toggleFilter$ } =
    useRefinementsEvents();
  const { newsletterSubmit$ } = useNewsletterCallbacks();

  //#region dy-events
  if (isBrowser()) {
    document.addEventListener('DOMContentLoaded', () => {
      dynamicYield.fireDYIdentifyAPIEvent();
    });
  }

  React.useEffect(() => {
    const newsletterSubmitSub = newsletterSubmit$.subscribe((emailHashed) => {
      dynamicYield.fireNewsletterSubscriptionEvent(emailHashed);
    });

    return newsletterSubmitSub.unsubscribe;
  }, [dynamicYield, newsletterSubmit$]);

  React.useEffect(() => {
    const clearAllSub = clearAll$.subscribe(dynamicYield.fireClearAllFiltersEvent);
    return clearAllSub.unsubscribe;
  }, [clearAll$, dynamicYield]);

  React.useEffect(() => {
    const priceSortSub = priceSort$.subscribe(dynamicYield.fireSortItemsEvent);
    return priceSortSub.unsubscribe;
  }, [dynamicYield.fireSortItemsEvent, priceSort$]);

  React.useEffect(() => {
    const facetFilterSub = facetFilter$.subscribe(({ isActive, facetSeoName, valueSeoName }) => {
      if (!isActive) {
        dynamicYield.fireRemoveStringFilterEvent(facetSeoName, valueSeoName);
      } else {
        dynamicYield.fireFilterItemsByStringFacetEvent(facetSeoName, valueSeoName);
      }
    });

    return facetFilterSub.unsubscribe;
  }, [dynamicYield, dynamicYield.fireSortItemsEvent, facetFilter$]);

  React.useEffect(() => {
    const numericFilterSub = numericFilter$.subscribe(({ seoName, value }) => {
      if (!value) {
        dynamicYield.fireRemoveNumericFilterEvent(seoName);
      } else {
        dynamicYield.fireFilterItemsByNumericRangeFacetEvent(seoName, value.value.min, value.value.max);
      }
    });

    return numericFilterSub.unsubscribe;
  }, [dynamicYield, numericFilter$]);

  React.useEffect(() => {
    const toggleFilterSub = toggleFilter$.subscribe(({ seoName, value, isActive }) => {
      if (!isActive) {
        dynamicYield.fireRemoveStringFilterEvent(seoName, `${value}`);
      } else {
        dynamicYield.fireFilterItemsByStringFacetEvent(seoName, `${value}`);
      }
    });

    return toggleFilterSub.unsubscribe;
  });

  //#endregion dy-events

  const addWishlist = (product: Product) => {
    gtmEventDispatcher.addToWishlist({
      productId: product.productId,
      isLocallyFiltered: false, // TODO: fix when local filters are available
      title: product.title,
    });
    // wishlist event

    dynamicYield.fireAddToWishlistEvent(product.productId);
  };

  const removeFromWishlist = (product: Product) => {
    gtmEventDispatcher.removeFromWishlist({
      productId: product.productId,
      isLocallyFiltered: false, // TODO: fix when local filters are available
      title: product.title,
    });
    // add and remove events for wishlist use same payload for prudsys
    // TODO: ask for quantities

    dynamicYield.fireRemoveFromWishlistEvent(product.productId);
  };

  const productClickout = (params: ProductClickoutParams) => {
    const { product, segment, trackingToken, queryID = '', indexName, index } = params;
    gtmEventDispatcher.productClickout(product);
    if (segment !== ClickoutSegment.RECOMMENDATION) {
      // product view event
    } else {
      // recommendation click
    }

    if (PRODUCTS_PERSONALIZATION_PROVIDER === 'algolia') {
      const trackingAgent = trackingAgentBySearchIndex(indexName as AlgoliaIndiceName);
      if (segment === ClickoutSegment.SEARCH) {
        trackingAgent.clickedObjectIDsAfterSearch({
          index: indexName,
          objectIDs: [product.objectID],
          positions: [index + 1],
          queryID,
        });
      } else {
        trackingAgent.clickedObjectIDs({
          index: indexName,
          objectIDs: [product.objectID],
        });
      }
    }

    dyApiClient.pageView(product.productId, { context }).catch((e) => console.error('clickout dy pageView error', e));
    dynamicYieldApi.firePurchaseEvent(product.productId);

    dynamicYield.fireClickoutEvent(product.productId);
  };

  React.useEffect(() => {
    const priceSortSub = priceSort$.subscribe(dynamicYield.fireSortItemsEvent);
    return priceSortSub.unsubscribe;
  }, [dynamicYield.fireSortItemsEvent, priceSort$]);

  React.useEffect(() => {
    const refinementsChangedSub = refinementsChangedDeep$.subscribe((refs) => {
      if (PRODUCTS_PERSONALIZATION_PROVIDER === 'algolia' && refs) {
        const { facetFilters, numericFilters } = mapRefinementsToAlgoliaQuery(refs);
        const indexName = productSearchResponse?.indexUsed || AlgoliaIndiceName.PRODUCTS;
        const trackingAgent = trackingAgentBySearchIndex(indexName as AlgoliaIndiceName);
        trackingAgent.convertedFilters({
          filters: [...(flatten(facetFilters) as string[]), ...(flatten(numericFilters) as any)].slice(0, 10),
          index: indexName,
        });
      }
    });

    return refinementsChangedSub.unsubscribe;
  }, [PRODUCTS_PERSONALIZATION_PROVIDER, productSearchResponse, refinementsChangedDeep$, trackingAgentBySearchIndex]);

  const marketing = gtmEventDispatcher.marketing;
  const productLoadMore = gtmEventDispatcher.productLoadMore;
  const productSearchStateUpdate = (refinements: Refinements) => {
    gtmEventDispatcher.productSearchStateUpdate(refinements);
  };

  const searchSubmit = (searchTerm: string) => {
    dynamicYield.fireSearchEvent(searchTerm);
  };

  const searchQuerySuggestionSubmit = (searchTerm: string) => {
    dynamicYield.fireSearchEvent(searchTerm);
  };

  const recommendationRequest = (service: string, prudsysTestGroup: string) => {
    gtmEventDispatcher.recommendationRequest({
      service,
      prudsysTestGroup,
    });
  };

  const cmsModuleEvent = (data: { [key: string]: any }) => gtmEventDispatcher.cmsModuleEvent(data);

  const merchantAdLoad = (merchantAdData: LocalDealerData) => {
    gtmEventDispatcher.merchantAdLoaded({ ...merchantAdData, merchantCity: merchantAdData.city });
  };
  const merchantClickExternal = (event: MerchantAdClickedEvent) => {
    gtmEventDispatcher.merchantAdClickedExternal(event);
  };
  const merchantClickInternal = (event: MerchantAdClickedEvent) => {
    gtmEventDispatcher.merchantAdClickedInternal(event);
  };

  const newsletterRegistered = () => gtmEventDispatcher.newsletterRegistered();

  const value = React.useRef({
    addWishlist,
    removeFromWishlist,
    marketing,
    merchantAdLoad,
    merchantClickExternal,
    merchantClickInternal,
    searchSubmit,
    searchQuerySuggestionSubmit,
    productClickout,
    productLoadMore,
    productSearchStateUpdate,
    recommendationRequest,
    cmsModuleEvent,
    newsletterRegistered,
  });
  return <SideEffectsContext.Provider value={value.current}>{children}</SideEffectsContext.Provider>;
};
